// Controller · Verantwoording — the COMPUTED accountability dashboard (PLAN-W27 C; FRONTEND-BLUEPRINT
// §3.4; docs/spec/tenant-controller/snapshots/tenant-controller-verantwoording.txt). Backed by GET
// /accountability/overview: the calc runs over (enabled regime × linked bewijscontainers × file
// validity at the as-of date) — an activity is "in orde" iff every linked container holds a document
// valid on the peildatum (snapshot §3.2/§5.2). Surface: the snapshot's filter strip (Begin/Einde
// maand+jaar, Hoofddoel, Activiteit, Toepassen), KPI cards (verantwoording % · in orde/totaal ·
// non-accountability · ontbrekende bewijzen), per-section progress, and the "Ontbrekende bewijzen"
// list (Activity code + name + Bewijsmiddel). On-demand point-in-time calc — the legacy daily-grained
// time-series chart (snapshot §3.3.2/§5.3) is a flagged follow-up, not faked here.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, FileWarning, Gauge, type LucideIcon } from "lucide-react";
import type { AccountabilityOverview, HoofddoelOption } from "@sympllex/api-client";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { Can } from "../../lib/rbac";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface CanonicalActivity {
  id: string; name: string; activityCode: string | null; sectionCode: string | null;
}
interface Filters {
  from: string; to: string;
  hoofddoelId?: string | undefined; activityId?: string | undefined;
}
interface MissingRow extends Record<string, unknown> {
  activityId: string; activityCode: string | null; activityName: string;
  containerId: string | null; containerName: string | null;
}

const pad2 = (n: number) => String(n).padStart(2, "0");
const ym = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const splitYm = (v: string): { y: string; m: string } => {
  const [y = "", m = ""] = v.split("-");
  return { y, m };
};

// Snapshot default: a 12-month window ending in the current month (Mei 2025 – April 2026 pattern).
function defaultFilters(now: Date): Filters {
  return { from: ym(new Date(now.getFullYear(), now.getMonth() - 11, 1)), to: ym(now) };
}

export function AccountabilityRoute() {
  const { t, lang } = useI18n();
  const locale = lang === "nl" ? "nl-NL" : "en-GB";

  const [draft, setDraft] = useState<Filters>(() => defaultFilters(new Date()));
  const [applied, setApplied] = useState<Filters>(draft);

  const overviewQ = useQuery({
    queryKey: ["accountability", applied.from, applied.to, applied.hoofddoelId ?? "", applied.activityId ?? ""],
    queryFn: () => api.getAccountabilityOverview(applied),
  });
  // Activiteit filter options — the canonical activities the Planner regime browses (existing endpoint).
  const activitiesQ = useQuery({
    queryKey: ["regime", "activities"],
    queryFn: () => api.listCanonicalActivities() as Promise<CanonicalActivity[]>,
  });

  const overview: (AccountabilityOverview & { hoofddoelen: HoofddoelOption[] }) | undefined = overviewQ.data;
  const loading = overviewQ.isLoading;
  const error = overviewQ.error;

  const monthNames = useMemo(() => {
    const f = new Intl.DateTimeFormat(locale, { month: "long" });
    return Array.from({ length: 12 }, (_, i) => f.format(new Date(2026, i, 1)));
  }, [locale]);
  const years = useMemo(() => {
    const last = new Date().getFullYear() + 1;
    return Array.from({ length: last - 2015 + 1 }, (_, i) => String(2015 + i));
  }, []);

  const fmtPct = (n: number) => `${n.toLocaleString(locale, { maximumFractionDigits: 1 })}%`;
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });

  const setPeriod = (which: "from" | "to", part: "y" | "m", value: string) =>
    setDraft((d) => {
      const cur = splitYm(d[which]);
      const next = part === "y" ? `${value}-${cur.m}` : `${cur.y}-${value}`;
      return { ...d, [which]: next };
    });

  const missingRows = useMemo<MissingRow[]>(
    () => (overview?.missingEvidence ?? []).map((m) => ({ ...m })),
    [overview?.missingEvidence],
  );

  const columns: Column<MissingRow>[] = [
    { key: "code", header: t("acc.col.code"), className: "w-24", render: (r) => <span className="font-mono text-xs">{r.activityCode ?? "—"}</span> },
    { key: "activity", header: t("acc.col.activity"), render: (r) => <span className="font-medium">{r.activityName}</span> },
    {
      key: "container", header: t("acc.col.container"),
      render: (r) => r.containerName ?? <span className="italic text-muted-foreground">{t("acc.missing.noContainer")}</span>,
    },
  ];

  // Graceful empty state: nothing enabled in the regime (and no narrowing filter active) → point the
  // Controller at Planner → Regime configureren.
  const emptyRegime =
    !loading && !error && overview != null && overview.totalEnabled === 0 && !applied.hoofddoelId && !applied.activityId;

  return (
    <Can perm="bewijscontainer.read" fallback={<p className="text-sm text-muted-foreground">{t("settings.readonly")}</p>}>
      <section>
        <PageHeader title={t("acc.title")} description={t("acc.sub")} help={t("help.accountability")} />

        {error != null && (
          <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {t("acc.error")} {(error as Error).message}
          </div>
        )}

        {/* Filter strip (snapshot §2.1): Begin/Einde maand+jaar · Hoofddoel · Activiteit · Toepassen */}
        <Card className="mb-6">
          <CardContent className="flex flex-wrap items-end gap-4">
            <PeriodSelect idPrefix="acc-from" labelMonth={t("acc.filter.beginMonth")} labelYear={t("acc.filter.beginYear")}
              value={draft.from} monthNames={monthNames} years={years}
              onMonth={(m) => setPeriod("from", "m", m)} onYear={(y) => setPeriod("from", "y", y)} />
            <PeriodSelect idPrefix="acc-to" labelMonth={t("acc.filter.endMonth")} labelYear={t("acc.filter.endYear")}
              value={draft.to} monthNames={monthNames} years={years}
              onMonth={(m) => setPeriod("to", "m", m)} onYear={(y) => setPeriod("to", "y", y)} />

            <div className="grid min-w-44 gap-1.5">
              <Label htmlFor="acc-hoofddoel">{t("acc.filter.hoofddoel")}</Label>
              <Select value={draft.hoofddoelId ?? "all"}
                onValueChange={(v) => setDraft((d) => ({ ...d, hoofddoelId: v === "all" ? undefined : v }))}>
                <SelectTrigger id="acc-hoofddoel" className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("acc.filter.all")}</SelectItem>
                  {(overview?.hoofddoelen ?? []).map((h) => (
                    <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid min-w-44 gap-1.5">
              <Label htmlFor="acc-activity">{t("acc.filter.activity")}</Label>
              <Select value={draft.activityId ?? "all"}
                onValueChange={(v) => setDraft((d) => ({ ...d, activityId: v === "all" ? undefined : v }))}>
                <SelectTrigger id="acc-activity" className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("acc.filter.all")}</SelectItem>
                  {(activitiesQ.data ?? []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.activityCode ? `${a.activityCode} — ${a.name}` : a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button onClick={() => setApplied(draft)} disabled={overviewQ.isFetching}>
              {t("acc.filter.apply")}
            </Button>
          </CardContent>
        </Card>

        {/* As-of line + honesty note: the calc is a point-in-time snapshot, the trend chart follows. */}
        {overview != null && (
          <p className="mb-6 text-xs text-muted-foreground">
            {t("acc.asOf")}: <span className="font-medium">{fmtDate(overview.asOf)}</span> · {t("acc.note.onDemand")}
          </p>
        )}

        {emptyRegime ? (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <p className="text-base font-medium">{t("acc.empty.title")}</p>
              <p className="max-w-md text-sm text-muted-foreground">{t("acc.empty.body")}</p>
              <Button asChild variant="outline">
                <Link to="/activities/regime">{t("acc.empty.cta")}</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* KPI cards (snapshot §3.3.1 + deliverable: overall %, in orde / totaal) */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <KpiCard icon={Gauge} label={t("acc.kpi.pct")} hint={t("acc.kpi.pct.hint")}
                value={overview ? fmtPct(overview.pct) : "—"} loading={loading} />
              <KpiCard icon={CheckCircle2} label={t("acc.kpi.inOrde")} hint={t("acc.kpi.inOrde.hint")}
                value={overview ? `${overview.inOrde} / ${overview.totalEnabled}` : "—"} loading={loading} />
              <KpiCard icon={AlertTriangle} label={t("acc.kpi.nonacc")} hint={t("acc.kpi.nonacc.hint")}
                value={overview ? fmtPct(overview.nonAccountabilityPct) : "—"} loading={loading}
                {...(overview && overview.nonAccountabilityPct > 0 ? { accent: "amber" as const } : {})} />
              <KpiCard icon={FileWarning} label={t("acc.kpi.missing")} hint={t("acc.kpi.missing.hint")}
                value={overview ? String(overview.missingEvidence.length) : "—"} loading={loading}
                {...(overview && overview.missingEvidence.length > 0 ? { accent: "red" as const } : {})} />
            </div>

            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Ontbrekende bewijzen (snapshot §4: Activity code + name + Bewijsmiddel) */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileWarning className="size-4 text-primary" aria-hidden />
                    {t("acc.missing.title")}
                  </CardTitle>
                  <CardDescription>{t("acc.missing.sub")}</CardDescription>
                </CardHeader>
                <CardContent>
                  <DataTable<MissingRow>
                    columns={columns}
                    rows={missingRows}
                    loading={loading}
                    getRowId={(r) => `${r.activityId}:${r.containerId ?? "none"}`}
                    empty={t("acc.missing.empty")}
                  />
                </CardContent>
              </Card>

              {/* Per-section rollup */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Gauge className="size-4 text-primary" aria-hidden />
                    {t("acc.sections.title")}
                  </CardTitle>
                  <CardDescription>{t("acc.sections.sub")}</CardDescription>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
                  ) : (overview?.sections.length ?? 0) === 0 ? (
                    <p className="text-sm text-muted-foreground">{t("acc.sections.empty")}</p>
                  ) : (
                    <ul className="space-y-3">
                      {(overview?.sections ?? []).map((s) => (
                        <li key={s.sectionCode ?? "none"}>
                          <div className="mb-1 flex items-center justify-between text-sm">
                            <span className="font-medium">
                              {s.sectionCode != null ? `${t("acc.section")} ${s.sectionCode}` : "—"}
                            </span>
                            <span className="tabular-nums text-muted-foreground">
                              {s.inOrde}/{s.total} · {fmtPct(s.pct)}
                            </span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                            <div className="h-full rounded-full bg-primary" style={{ width: `${s.pct}%` }} />
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </section>
    </Can>
  );
}

// ---- month + year select pair (the snapshot's Begin/Einde maand+jaar dropdowns) ---------------------
function PeriodSelect({
  idPrefix, labelMonth, labelYear, value, monthNames, years, onMonth, onYear,
}: {
  idPrefix: string; labelMonth: string; labelYear: string;
  /** "YYYY-MM" */ value: string;
  monthNames: string[]; years: string[];
  onMonth: (m: string) => void; onYear: (y: string) => void;
}) {
  const { y, m } = splitYm(value);
  return (
    <>
      <div className="grid min-w-36 gap-1.5">
        <Label htmlFor={`${idPrefix}-month`}>{labelMonth}</Label>
        <Select value={m} onValueChange={onMonth}>
          <SelectTrigger id={`${idPrefix}-month`} className="w-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            {monthNames.map((name, i) => (
              <SelectItem key={name} value={pad2(i + 1)}>{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid min-w-28 gap-1.5">
        <Label htmlFor={`${idPrefix}-year`}>{labelYear}</Label>
        <Select value={y} onValueChange={onYear}>
          <SelectTrigger id={`${idPrefix}-year`} className="w-full"><SelectValue /></SelectTrigger>
          <SelectContent>
            {years.map((yr) => <SelectItem key={yr} value={yr}>{yr}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

// ---- KPI card ----------------------------------------------------------------------------------------
function KpiCard({
  icon: Icon, label, hint, value, loading, accent,
}: {
  icon: LucideIcon; label: string; hint: string; value: string; loading?: boolean; accent?: "red" | "amber";
}) {
  return (
    <Card size="sm">
      <CardContent className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-muted-foreground">{label}</span>
          <Icon aria-hidden className={cn("size-4", accent === "red" ? "text-destructive" : accent === "amber" ? "text-amber-500" : "text-primary")} />
        </div>
        <span
          className={cn(
            "font-heading text-3xl font-semibold tabular-nums",
            loading && "animate-pulse text-muted-foreground/40",
            accent === "red" && "text-destructive",
          )}
        >
          {loading ? "—" : value}
        </span>
        <span className="text-xs text-muted-foreground">{hint}</span>
      </CardContent>
    </Card>
  );
}
